import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DndContext, closestCenter, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy } from "@dnd-kit/sortable";
import { Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { KPITileConfig } from "../../types/kpi-types";
import { ReportTestItem } from "../../utils/entities";
import { KPIConfigDialog } from "./kpi-config-dialog";
import { SortableKPITile } from "./sortable-kpi-tile";
import { TestSuite } from "store/src/lib/tests/entity";

interface CustomKPISectionProps {
  tiles: KPITileConfig[];
  data: ReportTestItem[];
  testSuites: TestSuite[];
  isEditMode: boolean;
  onUpdate: (tiles: KPITileConfig[]) => void;
}

export function CustomKPISection({
  tiles,
  data,
  testSuites,
  isEditMode,
  onUpdate,
}: CustomKPISectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTile, setEditingTile] = useState<KPITileConfig | undefined>();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = tiles.findIndex((tile) => tile.id === active.id);
    const newIndex = tiles.findIndex((tile) => tile.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newTiles = arrayMove(tiles, oldIndex, newIndex);
      onUpdate(newTiles);
    }
  }, [tiles, onUpdate]);

  const handleAdd = useCallback(() => {
    setEditingTile(undefined);
    setDialogOpen(true);
  }, []);

  const handleEdit = useCallback((config: KPITileConfig) => {
    setEditingTile(config);
    setDialogOpen(true);
  }, []);

  const handleRemove = useCallback((id: string) => {
    const newTiles = tiles.filter((tile) => tile.id !== id);
    onUpdate(newTiles);
  }, [tiles, onUpdate]);

  const handleSave = useCallback((config: KPITileConfig) => {
    if (editingTile) {
      // Update existing tile
      const newTiles = tiles.map((tile) =>
        tile.id === config.id ? config : tile
      );
      onUpdate(newTiles);
    } else {
      // Add new tile
      onUpdate([...tiles, config]);
    }
    setDialogOpen(false);
    setEditingTile(undefined);
  }, [editingTile, tiles, onUpdate]);

  if (!isEditMode && tiles.length === 0) {
    return null;
  }

  return (
    <>
      <section className="space-y-6 mb-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium">Custom KPIs</h2>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={tiles.map((tile) => tile.id)}
            strategy={rectSortingStrategy}
          >
            <div className="summary-grid grid grid-cols-2 gap-4 md:grid-cols-4 print:grid-cols-4 print:gap-3">
              {tiles.map((tile) => (
                <SortableKPITile
                  key={tile.id}
                  config={tile}
                  data={data}
                  isEditMode={isEditMode}
                  onEdit={handleEdit}
                  onRemove={handleRemove}
                />
              ))}

              {isEditMode && (
                <Card className="border-dashed border-2 border-zinc-300 bg-zinc-50 flex items-center justify-center min-h-[120px]">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleAdd}
                    className="flex flex-col items-center gap-2 h-full w-full"
                  >
                    <Plus className="h-6 w-6 text-zinc-500" />
                    <span className="text-sm text-zinc-500">Add KPI</span>
                  </Button>
                </Card>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </section>

      <KPIConfigDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        testSuites={testSuites}
        initialConfig={editingTile}
      />
    </>
  );
}

